<script setup>
import { computed, ref, watch, onMounted } from 'vue'
import { usePage, router } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import ChatwootWidget from '@/Components/Public/ChatwootWidget.vue'
import HeaderMenu from '@/Components/Public/HeaderMenu.vue'
import BrandLockup from '@/Components/UI/BrandLockup.vue'

const page = usePage()
const { t, locale } = useI18n()

const brand    = computed(() => page.props.brand || {})
const current  = computed(() => page.props.locale || 'en')
const flash    = computed(() => page.props.flash || {})

const footerChannels = computed(() => {
  const c = brand.value.channels || {}
  return [
    ...(c.message     || []),
    ...(c.social_page || []),
    ...(c.support     || []),
    ...(c.website     || [])
  ]
})

function platformLabel(p) {
  return p.charAt(0).toUpperCase() + p.slice(1)
}

watch(current, (val) => { locale.value = val }, { immediate: true })

function setLang(lang) {
  if (lang === current.value) return
  router.get(window.location.pathname, { lang }, { preserveScroll: true, preserveState: false })
}

// Display-currency picker. Server honours ?currency= and persists in a
// cookie, so the next page load keeps the choice without us managing state.
const displayCurrency   = computed(() => page.props.display_currency || 'THB')
const displayCurrencies = computed(() => page.props.display_currencies || ['THB'])
const hasCurrencyPicker = computed(() => displayCurrencies.value.length > 1)

function setCurrency(code) {
  if (code === displayCurrency.value) return
  router.get(window.location.pathname, { currency: code }, { preserveScroll: true, preserveState: false })
}

// Theme (light/dark). Public-site-only — stored in localStorage.gp_theme
// and applied via data-mode on documentElement. The boot script in
// application.html.erb reads gp_theme before paint to prevent flash.
const themeMode = ref('light')

function applyTheme(mode) {
  themeMode.value = mode
  if (typeof document === 'undefined') return
  document.documentElement.dataset.mode = mode
  document.documentElement.style.colorScheme = mode
  try { localStorage.setItem('gp_theme', mode) } catch (e) {}
}

function setTheme(mode) {
  if (mode === themeMode.value) return
  applyTheme(mode)
}

onMounted(() => {
  try {
    const stored = localStorage.getItem('gp_theme')
    if (stored === 'dark' || stored === 'light') {
      themeMode.value = stored
    } else if (document.documentElement.dataset.mode === 'dark') {
      themeMode.value = 'dark'
    }
  } catch (e) {}
})

const localeOptions = [
  { value: 'en', label: 'English', sub: 'EN' },
  { value: 'ru', label: 'Русский', sub: 'RU' },
  { value: 'zh', label: '中文',    sub: 'ZH' }
]
</script>

<template>
  <div class="public-shell">
    <header class="public-header">
      <div class="public-header__inner">
        <a href="/" class="public-brand" :aria-label="brand.name || 'Get Phuket'">
          <BrandLockup />
        </a>
        <nav class="public-nav">
          <a href="/rentals"  class="public-nav__link">{{ t('public.nav.rentals') }}</a>
          <a href="/property" class="public-nav__link">{{ t('public.nav.property') }}</a>
          <a href="/tours"    class="public-nav__link">{{ t('public.nav.tours') }}</a>
          <a href="/partners" class="public-nav__link">{{ t('public.nav.partners') }}</a>
        </nav>
        <div class="public-header__tools">
          <!-- Currency -->
          <HeaderMenu v-if="hasCurrencyPicker" :aria-label="t('public.menu.currency')">
            <template #icon>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="9"/>
                <path d="M14 9a2.5 2.5 0 0 0-2.5-2.5h-1A2.5 2.5 0 0 0 8 9c0 1.5 1 2.2 3 2.5s3 1 3 2.5a2.5 2.5 0 0 1-2.5 2.5h-1A2.5 2.5 0 0 1 8 14"/>
                <path d="M11 5v1.5M11 16.5V18"/>
              </svg>
            </template>
            <template #default="{ close }">
              <button v-for="code in displayCurrencies" :key="code" type="button" class="hmenu__item" @click="setCurrency(code); close()">
                <span>{{ code }}</span>
                <svg v-if="code === displayCurrency" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="hmenu__check" aria-hidden="true">
                  <polyline points="5 12 10 17 19 7"/>
                </svg>
              </button>
            </template>
          </HeaderMenu>

          <!-- Language -->
          <HeaderMenu :aria-label="t('public.menu.language')">
            <template #icon>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="9"/>
                <path d="M3 12h18"/>
                <path d="M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18"/>
              </svg>
            </template>
            <template #default="{ close }">
              <button v-for="opt in localeOptions" :key="opt.value" type="button" class="hmenu__item" @click="setLang(opt.value); close()">
                <span>{{ opt.label }} <span class="hmenu__item-sub">({{ opt.sub }})</span></span>
                <svg v-if="opt.value === current" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="hmenu__check" aria-hidden="true">
                  <polyline points="5 12 10 17 19 7"/>
                </svg>
              </button>
            </template>
          </HeaderMenu>

          <!-- Theme -->
          <HeaderMenu :aria-label="t('public.menu.theme')">
            <template #icon>
              <svg v-if="themeMode === 'dark'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z"/>
              </svg>
              <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="4"/>
                <path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M5.6 18.4l1.4-1.4M17 7l1.4-1.4"/>
              </svg>
            </template>
            <template #default="{ close }">
              <button type="button" class="hmenu__item" @click="setTheme('light'); close()">
                <span class="hmenu__item-row">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="hmenu__leadicon" aria-hidden="true">
                    <circle cx="12" cy="12" r="4"/>
                    <path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M5.6 18.4l1.4-1.4M17 7l1.4-1.4"/>
                  </svg>
                  {{ t('public.menu.theme_light') }}
                </span>
                <svg v-if="themeMode === 'light'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="hmenu__check" aria-hidden="true">
                  <polyline points="5 12 10 17 19 7"/>
                </svg>
              </button>
              <button type="button" class="hmenu__item" @click="setTheme('dark'); close()">
                <span class="hmenu__item-row">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="hmenu__leadicon" aria-hidden="true">
                    <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z"/>
                  </svg>
                  {{ t('public.menu.theme_dark') }}
                </span>
                <svg v-if="themeMode === 'dark'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="hmenu__check" aria-hidden="true">
                  <polyline points="5 12 10 17 19 7"/>
                </svg>
              </button>
            </template>
          </HeaderMenu>
        </div>
      </div>
    </header>

    <div v-if="flash.alert" role="alert" class="pub-flash pub-flash--alert">
      {{ flash.alert }}
    </div>
    <div v-if="flash.notice" role="status" class="pub-flash pub-flash--notice">
      {{ flash.notice }}
    </div>

    <main class="public-main">
      <slot />
    </main>

    <footer class="public-footer">
      <div class="public-footer__inner">
        <span>© {{ new Date().getFullYear() }} {{ brand.name || 'Get Phuket' }}</span>
        <template v-for="c in footerChannels" :key="c.id">
          <span class="public-footer__dot">·</span>
          <a :href="c.url" target="_blank" rel="noopener">{{ c.label || platformLabel(c.platform) }}</a>
        </template>
      </div>
    </footer>
    <ChatwootWidget />
  </div>
</template>

<style scoped>
.public-shell {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background:
    radial-gradient(ellipse 80% 60% at 50% -10%, color-mix(in oklch, var(--accent) 12%, transparent) 0%, transparent 60%),
    var(--bg);
  color: var(--ink);
}

.public-header {
  padding: 1.25rem clamp(1.25rem, 4vw, 3rem);
}
.public-header__inner {
  max-width: 96rem;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.public-brand {
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  color: var(--ink);
}

.public-nav {
  display: none;
  gap: 1.25rem;
  margin-right: auto;
  margin-left: 2rem;
  font-size: 0.9375rem;
}
@media (min-width: 640px) { .public-nav { display: inline-flex; } }
.public-nav__link {
  color: var(--inkSoft);
  text-decoration: none;
  font-weight: 500;
}
.public-nav__link:hover { color: var(--ink); }

.public-header__tools {
  display: inline-flex;
  align-items: center;
  gap: 0.125rem;
  padding-left: 0.5rem;
  border-left: 1px solid color-mix(in oklch, var(--line) 80%, transparent);
}

/* Slot-projected styles target descendants of HeaderMenu's panel */
.public-header__tools :deep(.hmenu__item) {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
  padding: 0.55rem 0.75rem;
  background: transparent;
  border: 0;
  border-radius: 0.4rem;
  color: var(--ink);
  font: inherit;
  font-size: 0.875rem;
  text-align: left;
  cursor: pointer;
  transition: background 0.1s;
}
.public-header__tools :deep(.hmenu__item:hover) {
  background: color-mix(in oklch, var(--ink) 6%, transparent);
}
.public-header__tools :deep(.hmenu__item-row) {
  display: inline-flex;
  align-items: center;
  gap: 0.625rem;
}
.public-header__tools :deep(.hmenu__item-sub) {
  color: var(--muted);
  font-size: 0.8125rem;
  margin-left: 0.125rem;
}
.public-header__tools :deep(.hmenu__leadicon) {
  width: 1rem;
  height: 1rem;
  color: var(--inkSoft);
}
.public-header__tools :deep(.hmenu__check) {
  width: 1rem;
  height: 1rem;
  color: var(--accent);
  flex-shrink: 0;
}

.public-main {
  flex: 1;
  display: block;
}

.public-footer {
  padding: 1.5rem clamp(1.25rem, 4vw, 3rem);
  border-top: 1px solid color-mix(in oklch, var(--line) 60%, transparent);
}
.public-footer__inner {
  max-width: 96rem;
  margin: 0 auto;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
  color: var(--muted);
}
.public-footer a {
  color: var(--muted);
  text-decoration: none;
}
.public-footer a:hover { color: var(--ink); }
.public-footer__dot { opacity: 0.5; }

.pub-flash {
  padding: 0.75rem 1rem;
  margin: 1rem;
  border-radius: 0.5rem;
  font-size: 0.9375rem;
}
.pub-flash--alert {
  background: color-mix(in oklch, var(--danger) 12%, var(--surface));
  color: var(--danger);
  border: 1px solid color-mix(in oklch, var(--danger) 30%, transparent);
}
.pub-flash--notice {
  background: color-mix(in oklch, var(--ok) 12%, var(--surface));
  color: var(--ok);
  border: 1px solid color-mix(in oklch, var(--ok) 30%, transparent);
}
</style>
